/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  PaperPlaneRight, Envelope, Phone, MapPin, Clock, ShieldCheck,
  Buildings, Users, ArrowRight,
} from "@phosphor-icons/react";
import { MarketingNav, MarketingFooter, Eyebrow, BTN_GREEN } from "./_shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  useEffect(() => {
    document.title = "Contact · Rescigo Health";
    window.scrollTo(0, 0);
  }, []);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organisation: "",
    organization_type: "Enterprise", interest_type: "general", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/public/leads`, {
        ...form,
        role: form.organization_type?.toLowerCase() || "enterprise",
        source: "contact_page",
      });
      toast.success("Thank you — we'll get back within 1 business day.");
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again or email care@rescigo.in");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="page-contact">
      <MarketingNav/>

      <section className="relative pt-36 pb-20 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 text-center max-w-3xl">
          <div className="mx-auto"><Eyebrow>Talk to us</Eyebrow></div>
          <h1 className="font-heading text-5xl md:text-6xl mt-4 text-slate-900 leading-[1.05]">
            Let's build something <span className="italic text-[#2D5A27]">measurable</span>.
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-xl mx-auto leading-relaxed">
            Enterprise inquiries, government partnerships, franchise opportunities,
            investor relations. We reply within one business day.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-5 gap-10">
          {/* Left — direct contact */}
          <div className="md:col-span-2 space-y-5">
            <ContactCard Icon={Buildings} title="Enterprise & Government" body="care@rescigo.in"
                         sub="Sales, demos, partnership proposals"/>
            <ContactCard Icon={ShieldCheck} title="Security & Compliance" body="care@rescigo.in"
                         sub="Responsible disclosure, audit requests"/>
            <ContactCard Icon={Users} title="Investor Relations" body="ceo@rescigo.in"
                         sub="Fundraising, data-room, quarterly updates"/>
            <ContactCard Icon={Phone} title="Phone · Kerala" body="+91 73 0608 7704"
                         sub="Mon–Sat · 09:30–17:00 IST"/>
            <ContactCard Icon={MapPin} title="HQ" body="Trivandrum · Kerala · India"
                         sub="Operations centre + field staff nucleus"/>
          </div>

          {/* Right — form */}
          <div className="md:col-span-3 bg-white rounded-2xl border border-slate-200 p-8">
            {sent ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
                  <PaperPlaneRight size={24} weight="duotone" className="text-emerald-700"/>
                </div>
                <div className="font-heading text-2xl text-slate-900">Thank you.</div>
                <div className="text-slate-600 mt-2">We'll be in touch within one business day.</div>
                <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2D5A27]">
                  Back to homepage <ArrowRight size={12}/>
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-3">
                  <CF label="Your name" v={form.name} on={(v) => setForm({ ...form, name: v })} required testid="contact-name"/>
                  <CF label="Work email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} required testid="contact-email"/>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <CF label="Phone (+91)" v={form.phone} on={(v) => setForm({ ...form, phone: v })} required testid="contact-phone"/>
                  <CF label="Organisation" v={form.organisation} on={(v) => setForm({ ...form, organisation: v })}/>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <CS label="Organization type" v={form.organization_type}
                      on={(v) => setForm({ ...form, organization_type: v })}
                      opts={["Enterprise", "Hospital", "Government", "Institution", "Franchise", "Investor", "Other"]}/>
                  <CS label="What brings you here?" v={form.interest_type}
                      on={(v) => setForm({ ...form, interest_type: v })}
                      opts={[
                        ["general", "General inquiry"],
                        ["demo", "Request a demo"],
                        ["franchise", "Franchise opportunity"],
                        ["government", "Government partnership"],
                        ["investor", "Investor relations"],
                        ["media", "Media / press"],
                        ["careers", "Careers"],
                      ]}/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">Message</label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5}
                            required data-testid="contact-message"
                            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800"/>
                </div>
                <div className="pt-3 border-t border-slate-100 flex items-center gap-3">
                  <button type="submit" disabled={busy} data-testid="contact-submit" className={`${BTN_GREEN} disabled:opacity-60`}>
                    <PaperPlaneRight size={14} weight="fill"/> {busy ? "Sending…" : "Send message"}
                  </button>
                  <div className="text-[11px] text-slate-500 flex-1">
                    By submitting, you accept our <Link to="/privacy" className="underline">privacy policy</Link>.
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <MarketingFooter/>
    </div>
  );
}

const ContactCard = ({ Icon, title, body, sub }) => (
  <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/60">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#2D5A27]/10 text-[#2D5A27] grid place-items-center flex-none">
        <Icon size={15} weight="duotone"/>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{title}</div>
        <div className="font-medium text-slate-900 mt-1">{body}</div>
        <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
      </div>
    </div>
  </div>
);

const CF = ({ label, v, on, type = "text", required, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">{label}</label>
    <input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
           required={required} data-testid={testid}
           className="w-full h-11 px-3 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800"/>
  </div>
);

const CS = ({ label, v, on, opts }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">{label}</label>
    <select value={v} onChange={(e) => on(e.target.value)}
            className="w-full h-11 px-3 border border-slate-300 rounded-md text-sm bg-white outline-none focus:border-slate-800">
      {opts.map(o => {
        const [val, lbl] = Array.isArray(o) ? o : [o, o];
        return <option key={val} value={val}>{lbl}</option>;
      })}
    </select>
  </div>
);
